import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { QueryOptions, MutationOptions } from "@apollo/client";
import { ApolloGraphQlService } from "../apollo/apollo-graphql.service";
import gql from "graphql-tag";
import { timeDays } from "d3";
import { INSERT } from "@angular/cdk/keycodes";

const GET_TUNDISH = gql`
  {
    findUsuarios {
      uuid
      usuario
      nombre
      apellidos
      rol
      dispositivos {
        uuid
        nombre
      }
    }
  }
`;

const GET_CAMPAIGN_TUNDISH = gql`
  query findCampaignsByTundish($td: String, $disp: String) {
    findCampaignsByDisp(td: $td, disp: $disp) {
      inicio
      fin
      disp
      duracion
      estado
      nombre
      _id
    }
  }
`;

const GET_CAMPAIGN_BY_ID = gql`
  query findCampaignById($td: String, $id: String) {
    findCampaignById(td: $td, id: $id) {
      inicio
      fin
      disp
      duracion
      estado
      nombre
      _id
    }
  }
`;

const GET_CONSUMPTION_TUNDISH = gql`
  query findConsumptionsByTundish($td: String, $disp: String) {
    findConsumptionsByDisp(td: $td, disp: $disp) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_CONSUMPTION_BY_ID = gql`
  query findConsumptionById($td: String, $id: String) {
    findConsumptionById(td: $td, id: $id) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_OPERATIONS_TUNDISH = gql`
  query findOperationsByTundish($td: String, $disp: String) {
    findIntervals(td: $td, disp: $disp) {
      disp
      inicio
      fin
      campos {
        tundish
        arena {
          kilos
        }
        catalizador {
          litros
          relacion
        }
        silicato {
          litros
          relacion
        }
      }
      _id
    }
  }
`;

const GET_OPERATIONS_TUNDISH_TIME = gql`
  query findOperationsBetweenDates(
    $td: String
    $disp: String
    $inicio: Float
    $fin: Float
  ) {
    findIntervalsBetweenDates(
      td: $td
      disp: $disp
      inicio: $inicio
      fin: $fin
    ) {
      disp
      inicio
      fin
      campos {
        tundish
        arena {
          kilos
        }
        catalizador {
          litros
          relacion
        }
        silicato {
          litros
          relacion
        }
      }
      _id
    }
  }
`;

const GET_CONSUMPTION_TUNDISH_TIME = gql`
  query findConsumptionsBetweenDates(
    $td: String
    $disp: String
    $inicio: Float
    $fin: Float
  ) {
    findConsumptionsByDispBetweenDates(
      td: $td
      disp: $disp
      inicio: $inicio
      fin: $fin
    ) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_OPERATION_BY_ID = gql`
  query findOperationById($td: String, $id: String) {
    findIntervalById(td: $td, id: $id) {
      disp
      inicio
      fin
      campos {
        tundish
        arena {
          kilos
        }
        catalizador {
          litros
          relacion
        }
        silicato {
          litros
          relacion
        }
      }
      _id
    }
  }
`;

const GET_DATA_TYPES = gql`
  query findDataTypes {
    findTipoDatos {
      uuid
      nombre
    }
  }
`;

const GET_TIME_SERIE = gql`
  query findTimeSerie(
    $dispositivo: [String]
    $from: Float
    $to: Float
    $tipoDato: [String]
  ) {
    findTimeSerie(
      dispositivo: $dispositivo
      from: $from
      to: $to
      tipoDato: $tipoDato
    ) {
      valor
      fecha
    }
  }
`;

const INSERT_CAMPAIGN = gql`
  mutation insertCampaign(
    $td: String
    $disp: String
    $nombre: String
    $inicio: Float
    $fin: Float
  ) {
    insertCampaign(
      td: $td
      disp: $disp
      nombre: $nombre
      inicio: $inicio
      fin: $fin
    ) {
      insertedId
    }
  }
`;

const DELETE_CAMPAIGN = gql`
  mutation deleteCampaign($td: String, $disp: String, $id: String) {
    deleteCampaign(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_CAMPAIGN = gql`
  mutation updateCampaign(
    $td: String
    $disp: String
    $id: String
    $nombre: String
    $inicio: Float
    $fin: Float
  ) {
    updateCampaign(
      td: $td
      disp: $disp
      id: $id
      nombre: $nombre
      inicio: $inicio
      fin: $fin
    ) {
      ok
    }
  }
`;

const INSERT_CONSUMPTION = gql`
  mutation insertConsumption(
    $td: String
    $disp: String
    $consumo: Int
    $fecha: Float
    $coladas: Int
  ) {
    insertConsumption(
      td: $td
      disp: $disp
      consumo: $consumo
      fecha: $fecha
      coladas: $coladas
    ) {
      insertedId
    }
  }
`;

const DELETE_CONSUMPTION = gql`
  mutation deleteConusmption($td: String, $disp: String, $id: String) {
    deleteConsumption(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_CONSUMPTION = gql`
  mutation updateConsumption(
    $td: String
    $disp: String
    $id: String
    $consumo: Int
    $coladas: Int
    $fecha: Float
  ) {
    updateConsumption(
      td: $td
      disp: $disp
      id: $id
      consumo: $consumo
      coladas: $coladas
      fecha: $fecha
    ) {
      ok
    }
  }
`;

const UPDATE_OPERATION_BY_ID = gql`
  mutation updateIntervalById(
    $td: String
    $disp: String
    $ndisp: Int
    $dato: String
    $id: String
    $inicio: Float
    $fin: Float
    $arena: Float
    $catalizador: Float
    $silicato: Float
  ) {
    updateIntervalById(
      td: $td
      disp: $disp
      ndisp: $ndisp
      dato: $dato
      id: $id
      inicio: $inicio
      fin: $fin
      arena: $arena
      catalizador: $catalizador
      silicato: $silicato
    ) {
      ok
    }
  }
`;

const ASSIGN_DISP_TO_OPERATION_BY_ID = gql`
  mutation assignDispToInterval(
    $td: String
    $disp: String
    $ndisp: Int
    $dato: String
    $id: String
  ) {
    assignDispToInterval(
      td: $td
      disp: $disp
      ndisp: $ndisp
      dato: $dato
      id: $id
    ) {
      ok
    }
  }
`;

const GET_DATO_BY_DISP = gql`
  query findEstructura($uuid: String) {
    findEstructura(uuid: $uuid) {
      tiene {
        uuid
        es {
          nombre
        }
      }
    }
  }
`;

@Injectable({
  providedIn: "root",
})
export class TundishService {
  constructor(
    private apollo: Apollo,
    private http: HttpClient,
    private apolloService: ApolloGraphQlService
  ) {}

  getTundishOperationsFromJSONFile(): Observable<any> {
    return this.http.get("../assets/data/tundishMockup.json");
  }

  getTundishTypeDevices(): Observable<any> {
    return this.apollo.query<any>({
      query: GET_TUNDISH,
      fetchPolicy: "network-only",
    });
  }

  getCampaignsByTundish(td: String, disp: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CAMPAIGN_TUNDISH,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getCampaignById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CAMPAIGN_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getConsumptionsByTundish(td: String, disp: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CONSUMPTION_TUNDISH,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getConsumptionById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CONSUMPTION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationsByTundish(td: String, disp: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_OPERATIONS_TUNDISH,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationsByTundishBetweenDates(
    td: string,
    disp: string,
    inicio: number,
    fin: number
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_OPERATIONS_TUNDISH_TIME,
      variables: {
        td: td,
        disp: disp,
        inicio: inicio,
        fin: fin,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationById(td: string, id: string) {
    return this.apollo.query<any>({
      query: GET_OPERATION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getDataTypes(): Observable<any> {
    return this.apollo.query<any>({
      query: GET_DATA_TYPES,
      fetchPolicy: "network-only",
    });
  }

  getTimeseriesData(disp, td, from, to): Observable<any> {
    return this.apollo.query<any>({
      query: GET_TIME_SERIE,
      variables: {
        tipoDato: td,
        dispositivo: disp,
        from: from,
        to: to,
      },
      fetchPolicy: "network-only",
    });
  }

  insertCampaignByTundish(
    collection,
    disp,
    inicio,
    fin,
    nombre
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: INSERT_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        nombre: nombre,
      },
    });
  }

  deleteCampaignById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateCampaignById(
    collection,
    disp,
    id,
    inicio,
    fin,
    nombre
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        nombre: nombre,
      },
    });
  }

  getConsumptionsByTundishBetweenDates(
    td: string,
    disp: string,
    inicio: number,
    fin: number
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CONSUMPTION_TUNDISH_TIME,
      variables: {
        td: td,
        disp: disp,
        inicio: inicio,
        fin: fin,
      },
      fetchPolicy: "network-only",
    });
  }

  insertConsumptionByTundish(
    collection,
    disp,
    consumo,
    fecha,
    coladas
  ): Observable<any> {
    console.log(disp);
    return this.apollo.mutate<any>({
      mutation: INSERT_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
        coladas: coladas,
      },
    });
  }

  deleteConsumptionById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateConsumptionById(
    collection,
    disp,
    id,
    fecha,
    consumo,
    coladas
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
        coladas: coladas,
      },
    });
  }

  updateOperationById(
    collection,
    disp,
    ndisp,
    dato,
    id,
    inicio,
    fin,
    arena,
    catalizador,
    silicato
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_OPERATION_BY_ID,
      variables: {
        td: collection,
        disp: disp,
        ndisp: ndisp,
        dato: dato,
        id: id,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        arena: arena,
        catalizador: catalizador,
        silicato: silicato,
      },
    });
  }

  assignDispToOperationById(
    collection,
    disp,
    ndisp,
    dato,
    id,
    inicio,
    fin,
    arena,
    catalizador,
    silicato
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: ASSIGN_DISP_TO_OPERATION_BY_ID,
      variables: {
        td: collection,
        disp: disp,
        ndisp: ndisp,
        dato: dato,
        id: id,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        arena: arena,
        catalizador: catalizador,
        silicato: silicato,
      },
    });
  }

  getDatoByDisp(disp): Observable<any> {
    return this.apollo.query<any>({
      query: GET_DATO_BY_DISP,
      variables: {
        uuid: disp,
      },
    });
  }
}
